/**
 * storageBackend.ts — Abstracción de almacenamiento clave-valor para la capa
 * transaccional (ACID local).
 *
 * QUÉ HACE:
 *  - Define una interfaz única (`StorageBackend`) que aísla al motor de
 *    transacciones de la tecnología concreta de persistencia.
 *  - Provee la implementación síncrona sobre localStorage, reutilizando
 *    `getStorage`/`setStorage` (que ya manejan recuperación de cuota, anti
 *    pérdida de datos).
 *  - Expone `getActiveBackend()` como punto único de selección de backend.
 *
 * QUÉ EXPORTA: StorageBackend (tipo), localStorageBackend, getActiveBackend.
 *
 * QUÉ NO DEBE HACER:
 *  - No contener lógica de negocio (ventas, stock, empleados).
 *  - No importar React, hooks ni componentes.
 *  - No conocer el formato del journal (eso vive en writeAheadLog.ts).
 *
 * PUNTO DE EXTENSIÓN (fase híbrida → IndexedDB / nativo):
 *  Para conmutar a un backend más durable (IndexedDB vía persistenceService o
 *  sistema de archivos en Tauri), se implementa un nuevo objeto que cumpla
 *  `StorageBackend` y se retorna desde `getActiveBackend()` según configuración.
 *  El motor de transacciones (transactionalStore.ts) NO cambia. Nota: IndexedDB
 *  es asíncrono, por lo que ese backend requerirá la variante async del motor.
 */

import {
  getStorage,
  setStorage,
  setStorageSerialized,
  serializeForStorage,
} from "@/mocks/helpers/storage";
import { idbMirrorBackend } from "./idbMirrorBackend";

/**
 * Contrato mínimo que debe cumplir cualquier tecnología de persistencia para
 * participar en la capa transaccional. Todas las operaciones son síncronas en
 * la implementación actual (localStorage).
 *
 * Hay dos modos de valor:
 *  - JSON (`get`/`set`): serializa/parsea objetos y arrays de negocio.
 *  - Crudo (`getRaw`/`setRaw`): guarda strings escalares TAL CUAL, sin comillas
 *    JSON, para banderas/punteros legados que otros módulos leen con
 *    `localStorage.getItem` directo (ej. `caja_last_status`, `altenot_punto_caja_id`).
 */
export interface StorageBackend {
  /** Lee y parsea un valor JSON. Devuelve `defaultValue` si no existe o falla. */
  get<T>(key: string, defaultValue: T): T;
  /** Serializa y persiste un valor JSON de forma atómica por clave. */
  set<T>(key: string, value: T): void;
  /** Lee un string escalar sin parsear (formato crudo). null si no existe. */
  getRaw(key: string): string | null;
  /** Persiste un string escalar sin serializar (formato crudo). */
  setRaw(key: string, value: string): void;
  /**
   * Serializa un valor al string final de persistencia (plano o comprimido),
   * sin escribir. Permite serializar UNA vez y reutilizar el resultado para el
   * journal y la clave (evita doble JSON.stringify + compresión por commit).
   */
  serialize<T>(value: T): string;
  /**
   * Persiste un string YA serializado (salida de `serialize`) de forma atómica
   * por clave, con la misma maquinaria de cuota de `set`. `original` es el
   * valor sin serializar (opcional) y solo se usa para aligerarlo en un
   * reintento por cuota llena.
   */
  setSerialized<T>(key: string, serialized: string, original?: T): void;
  /** Elimina una clave. No falla si no existe. */
  remove(key: string): void;
}

/**
 * Backend síncrono sobre localStorage. Delega en los helpers existentes para
 * conservar la recuperación de cuota y el aviso anti pérdida de datos.
 */
export const localStorageBackend: StorageBackend = {
  get<T>(key: string, defaultValue: T): T {
    return getStorage<T>(key, defaultValue);
  },
  set<T>(key: string, value: T): void {
    setStorage<T>(key, value);
  },
  serialize<T>(value: T): string {
    return serializeForStorage(value);
  },
  setSerialized<T>(key: string, serialized: string, original?: T): void {
    setStorageSerialized<T>(key, serialized, original);
  },
  getRaw(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setRaw(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* cuota/entorno sin localStorage: ignorar (banderas no críticas) */
    }
  },
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      /* entorno sin localStorage (tests SSR): ignorar */
    }
  },
};

/**
 * Devuelve el backend activo. Punto único de conmutación para la fase híbrida.
 * Usa el backend dual-write (localStorage + IndexedDB mirror) para máxima
 * durabilidad en Android APK y desktop.
 */
export function getActiveBackend(): StorageBackend {
  return idbMirrorBackend;
}
